import React, { useEffect, useState } from 'react';
import { ChatAuthProvider, LoginScreen, useAuth } from './ChatAuth';
import { ChatApp } from './ChatApp';
import { Landing } from './Landing';
import { FeaturesPage } from './FeaturesPage';

// Magic links (OAuth handover, email verify, password reset) and invite links should
// drop straight into auth, skipping the marketing pages.
function hasAuthIntent(): boolean {
  const p = new URLSearchParams(window.location.search);
  return ['otc', 'reset', 'verified', 'login_error', 'invite'].some((k) => p.has(k));
}

// Persist an invite code the moment we see it, so it survives the sign-up → email-verify
// and OAuth redirects (which drop the query string). ChatApp consumes it after login.
function captureInvite(): void {
  const code = new URLSearchParams(window.location.search).get('invite');
  if (code) {
    try { localStorage.setItem('fxmchat_pending_invite', code); } catch { /* ignore */ }
  }
}

function currentPath(): string {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

function Gate() {
  const { user, loading, initial } = useAuth();
  const [path, setPath] = useState(currentPath());
  useEffect(() => {
    captureInvite();
    const onPop = () => setPath(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Client-side navigation. The SPA is served under one origin and nginx falls back to
  // index.html for any path, so /features and /login also load on a direct hit / refresh.
  const go = (to: string) => {
    if (window.location.pathname + window.location.search !== to) window.history.pushState({}, '', to);
    setPath(currentPath());
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="fx-login">
        <div className="fx-login-note">Loading…</div>
      </div>
    );
  }
  if (user) return <ChatApp />;

  const nav = {
    onHome: () => go('/'),
    onFeatures: () => go('/features'),
    onSignIn: () => go('/login'),
    onGetStarted: () => go('/login?signup=1'),
  };

  // Route into auth on STATE as well as URL: ChatAuth strips one-shot params
  // (?otc= etc.) from the URL before the redemption resolves, so by the time
  // loading finishes the URL alone says "landing" while a pending OAuth session
  // is waiting in context — that race used to dump OAuth users back on the
  // landing page and make them click Sign in twice.
  const authInFlight = !!(initial && (initial.pending || initial.resetToken || initial.notice || initial.error));
  if (hasAuthIntent() || authInFlight || path === '/login') {
    const signup = new URLSearchParams(window.location.search).has('signup');
    return <LoginScreen startStage={signup ? 'signup' : 'credentials'} onHome={() => go('/')} />;
  }
  if (path === '/features') {
    return <FeaturesPage {...nav} active="features" />;
  }
  return <Landing {...nav} active="home" />;
}

/** Entry point for the standalone chat app — own auth (JWT), own backend, no Matrix. */
export function ChatRoot() {
  return (
    <ChatAuthProvider>
      <Gate />
    </ChatAuthProvider>
  );
}
